import { type ReactElement, useEffect } from "react";

import { ApiError } from "@/lib/api-client";
import { type EnvName, readEnvName } from "@/lib/env";

const STORAGE_KEY = "endure:observability:browser-failures";
const STORE_LIMIT = 20;
const UPDATE_EVENT = "endure:observability:browser-failures";

let captureRefCount = 0;
let detachCaptureListeners: (() => void) | null = null;

export type BrowserFailureArea = "organization";
export type BrowserFailureSource = "browser" | "api";

export interface BrowserFailureItem {
  fingerprint: string;
  area: BrowserFailureArea;
  source: BrowserFailureSource;
  severity: "error";
  title: string;
  message: string;
  route: string;
  environment: EnvName;
  targetEnvironment: string;
  runtime: "local-browser";
  firstSeen: string;
  lastSeen: string;
  eventCount: number;
  action: string | null;
  statusCode: number | null;
  requestId: string | null;
  requestPath: string | null;
}

interface RecordBrowserFailureInput {
  area: BrowserFailureArea;
  source: BrowserFailureSource;
  title: string;
  message: string;
  action?: string;
  route?: string;
  statusCode?: number | null;
  requestId?: string | null;
  requestPath?: string | null;
}

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

function readTargetEnvironment(): string {
  const apiUrl = String(import.meta.env.VITE_API_URL ?? "").toLowerCase();

  if (apiUrl.includes("staging")) return "staging";
  if (apiUrl.includes("prod") || apiUrl.includes("production")) return "prod";
  if (apiUrl.includes("integration")) return "integration";
  if (apiUrl.includes("dev")) return "dev";

  return readEnvName();
}

function currentRoute(): string {
  if (!hasWindow()) return "unknown-route";

  const path = window.location.pathname || "unknown-route";
  if (path.startsWith("/organization")) return path;
  if (path.startsWith("/invitations/")) return "/invitations/:token/accept";

  return path || "unknown-route";
}

function normalizeRoute(route?: string): string {
  const value = sanitizeText(route ?? currentRoute(), 120);
  return value.length > 0 ? value : "unknown-route";
}

function sanitizeText(value: unknown, maxLength: number): string {
  if (typeof value !== "string") return "";
  return value.replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function isOrganizationRoute(route: string): boolean {
  return route.startsWith("/organization") || route.startsWith("/invitations/");
}

function readStore(): BrowserFailureItem[] {
  if (!hasWindow()) return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as BrowserFailureItem[]) : [];
  } catch {
    return [];
  }
}

function writeStore(items: BrowserFailureItem[]): void {
  if (!hasWindow()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, STORE_LIMIT)));
  } catch {
    // Storage may be unavailable in private or restricted environments.
  }
}

function publishUpdate(): void {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
}

function buildFingerprint(input: RecordBrowserFailureInput, route: string): string {
  return [
    input.area,
    input.source,
    input.action ?? input.title,
    input.statusCode ?? "na",
    input.requestPath ?? "no-path",
    route,
  ].join(":");
}

function upsertFailure(
  items: BrowserFailureItem[],
  next: BrowserFailureItem
): BrowserFailureItem[] {
  const existing = items.find((item) => item.fingerprint === next.fingerprint);

  if (!existing) {
    return [next, ...items].slice(0, STORE_LIMIT);
  }

  const updated: BrowserFailureItem = {
    ...existing,
    lastSeen: next.lastSeen,
    eventCount: existing.eventCount + 1,
    message: next.message,
    requestId: next.requestId ?? existing.requestId,
    statusCode: next.statusCode ?? existing.statusCode,
  };

  return [updated, ...items.filter((item) => item.fingerprint !== next.fingerprint)].slice(
    0,
    STORE_LIMIT
  );
}

export function readBrowserFailures(area?: BrowserFailureArea): BrowserFailureItem[] {
  const items = readStore();
  return area ? items.filter((item) => item.area === area) : items;
}

export function clearBrowserFailures(area?: BrowserFailureArea): void {
  const items = readStore();
  const nextItems = area ? items.filter((item) => item.area !== area) : [];
  writeStore(nextItems);
  publishUpdate();
}

export function subscribeBrowserFailures(listener: () => void): () => void {
  if (!hasWindow()) return () => undefined;

  window.addEventListener(UPDATE_EVENT, listener);
  window.addEventListener("storage", listener);

  return () => {
    window.removeEventListener(UPDATE_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}

export function recordBrowserFailure(input: RecordBrowserFailureInput): void {
  const route = normalizeRoute(input.route);
  if (!isOrganizationRoute(route)) return;

  const now = new Date().toISOString();
  const item: BrowserFailureItem = {
    fingerprint: buildFingerprint(input, route),
    area: input.area,
    source: input.source,
    severity: "error",
    title: sanitizeText(input.title, 120) || "Browser failure",
    message: sanitizeText(input.message, 240) || "Unknown browser failure",
    route,
    environment: readEnvName(),
    targetEnvironment: readTargetEnvironment(),
    runtime: "local-browser",
    firstSeen: now,
    lastSeen: now,
    eventCount: 1,
    action: sanitizeText(input.action, 120) || null,
    statusCode: input.statusCode ?? null,
    requestId: sanitizeText(input.requestId, 120) || null,
    requestPath: sanitizeText(input.requestPath, 160) || null,
  };

  writeStore(upsertFailure(readStore(), item));
  publishUpdate();
}

export function recordOrganizationApiFailure(params: {
  action: string;
  requestPath: string;
  error: unknown;
}): void {
  const message =
    params.error instanceof Error ? params.error.message : "Organization API request failed";
  const statusCode = params.error instanceof ApiError ? params.error.status : null;
  const requestId = params.error instanceof ApiError ? params.error.requestId : null;

  recordBrowserFailure({
    area: "organization",
    source: "api",
    title: params.action,
    message,
    action: params.action,
    statusCode,
    requestId,
    requestPath: params.requestPath,
  });
}

function errorMessageFromEvent(event: ErrorEvent): string {
  if (event.error instanceof Error) {
    return event.error.message || event.message || "Unhandled browser error";
  }

  return event.message || "Unhandled browser error";
}

function errorTitleFromEvent(event: ErrorEvent): string {
  if (event.error instanceof Error && event.error.name) {
    return event.error.name;
  }

  return "Browser runtime error";
}

function rejectionMessage(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message || reason.name;
  }

  if (typeof reason === "string") {
    return reason;
  }

  return "Unhandled promise rejection";
}

function attachBrowserFailureCapture(): () => void {
  if (!hasWindow()) return () => undefined;

  const handleError = (event: ErrorEvent): void => {
    recordBrowserFailure({
      area: "organization",
      source: "browser",
      title: errorTitleFromEvent(event),
      message: errorMessageFromEvent(event),
      action: "window.error",
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    recordBrowserFailure({
      area: "organization",
      source: "browser",
      title: "Unhandled promise rejection",
      message: rejectionMessage(event.reason),
      action: "window.unhandledrejection",
    });
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  return () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  };
}

export function retainBrowserFailureCapture(): () => void {
  captureRefCount += 1;

  if (captureRefCount === 1) {
    detachCaptureListeners = attachBrowserFailureCapture();
  }

  return () => {
    captureRefCount = Math.max(0, captureRefCount - 1);
    if (captureRefCount === 0 && detachCaptureListeners) {
      detachCaptureListeners();
      detachCaptureListeners = null;
    }
  };
}

export function BrowserObservabilityBootstrap(): ReactElement | null {
  useEffect(() => retainBrowserFailureCapture(), []);
  return null;
}
